import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useMemo } from 'react';
import { SmartLinkStatus } from '../../constants';
import Container from './components/container';
import { FlexibleUiAnalyticsContext, FlexibleUiContext, FlexibleUiOptionContext } from '../../state/flexible-ui-context';
import { getContextByStatus, getRetryOptions } from './utils';
import { useAISummaryConfig } from '../../state/hooks/use-ai-summary-config';

/**
 * This represents a Flexible Card: a link represented by a card with metadata.
 * This is the container in which all Flexible UI Blocks and Elements exist.
 * Note: TitleBlock is mandatory for a Flexible Card to render.
 * @see Container
 */
const FlexibleCard = ({
  analytics,
  appearance = 'flexible',
  cardState,
  children,
  id,
  onAuthorize,
  onClick,
  onError,
  onResolve,
  renderers,
  showAuthTooltip,
  showHoverPreview,
  hoverPreviewOptions,
  actionOptions,
  testId,
  ui,
  url
}) => {
  var _details$meta2;
  const aiSummaryConfig = useAISummaryConfig();
  const {
    status: cardType,
    details
  } = cardState;
  const status = cardType;
  const context = useMemo(() => getContextByStatus({
    aiSummaryConfig,
    response: details,
    id,
    renderers,
    actionOptions,
    status,
    url
  }), [aiSummaryConfig, details, id, renderers, actionOptions, status, url]);
  const retry = getRetryOptions(url, status, details, onAuthorize);
  const {
    title
  } = context || {};
  const analyticsContext = useMemo(() => {
    var _details$meta;
    return analytics ? {
      ...analytics,
      display: appearance,
      extensionKey: details === null || details === void 0 ? void 0 : (_details$meta = details.meta) === null || _details$meta === void 0 ? void 0 : _details$meta.key
    } : undefined;
  }, [analytics, appearance, details === null || details === void 0 ? void 0 : (_details$meta2 = details.meta) === null || _details$meta2 === void 0 ? void 0 : _details$meta2.key]);
  useEffect(() => {
    switch (status) {
      case SmartLinkStatus.Resolved:
        if (onResolve) {
          onResolve({
            title,
            url
          });
        }
        break;
      case SmartLinkStatus.Errored:
      case SmartLinkStatus.Fallback:
      case SmartLinkStatus.Forbidden:
      case SmartLinkStatus.NotFound:
      case SmartLinkStatus.Unauthorized:
        if (onError) {
          onError({
            status,
            url
          });
        }
        break;
    }
  }, [onError, onResolve, status, title, url]);
  return /*#__PURE__*/React.createElement(FlexibleUiAnalyticsContext.Provider, {
    value: analyticsContext
  }, /*#__PURE__*/React.createElement(FlexibleUiOptionContext.Provider, {
    value: ui
  }, /*#__PURE__*/React.createElement(FlexibleUiContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(Container, _extends({
    testId: testId
  }, ui, {
    onClick: onClick,
    retry: retry,
    showHoverPreview: showHoverPreview,
    hoverPreviewOptions: hoverPreviewOptions,
    actionOptions: actionOptions,
    showAuthTooltip: showAuthTooltip,
    status: status
  }), children))));
};
export default FlexibleCard;